/**
 * iter549 + iter551 — DashboardWidget
 *
 * Wrapper component that:
 *   • hides the widget when it's in `layout.hidden`
 *   • when `editMode` is ON, renders an overlay with:
 *       - red X button (top-right) → hides the widget
 *       - drag handle (top-left)   → shows the section can be moved
 *     plus a dashed border so the user clearly sees the editable area.
 *
 * The actual drag reordering between widgets happens inside the
 * customizer drawer (scope-based DnD). Inline mode focuses on the most
 * intuitive single action: remove a section directly from the dashboard.
 *
 * Usage:
 *   <DashboardWidget id="health-hub">
 *     <HealthHub ... />
 *   </DashboardWidget>
 */
import React from "react";
import { GripVertical, X } from "lucide-react";
import { useDashboardLayoutContext } from "@/dashboard/DashboardLayoutContext";
import { DASHBOARD_WIDGETS } from "@/dashboard/widgetRegistry";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import EmptySlot from "@/dashboard/EmptySlot";

export default function DashboardWidget({ id, children, as: As = "div", className }) {
  const { layout, setHidden, editMode } = useDashboardLayoutContext();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";

  const hidden = (layout?.hidden || []).includes(id);

  // iter552 — When hidden:
  //   • In normal mode → render nothing (the widget is invisible to the user).
  //   • In Edit Mode  → render an EmptySlot placeholder so the user can
  //                     immediately bring it back OR swap in another widget.
  if (hidden) {
    if (editMode) {
      return <EmptySlot widgetId={id} />;
    }
    return null;
  }

  const meta = DASHBOARD_WIDGETS.find((w) => w.id === id);
  const label = meta ? (bg ? meta.label_bg : meta.label_en) : id;

  const editStyles = editMode
    ? `relative ring-2 ring-dashed ${
        isDark ? "ring-violet-400/60" : "ring-violet-400"
      } rounded-2xl`
    : "";

  return (
    <As
      data-dashboard-widget={id}
      data-edit-mode={editMode ? "on" : "off"}
      className={`${className || ""} ${editStyles}`.trim()}
    >
      {editMode && (
        <div
          className="absolute -top-2.5 left-2 right-2 flex items-center justify-between gap-2 z-20 pointer-events-none"
          data-testid={`widget-inline-edit-${id}`}
        >
          <div
            className={`pointer-events-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full shadow-md text-[10px] font-bold uppercase tracking-wider ${
              isDark
                ? "bg-violet-500/90 text-white"
                : "bg-violet-600 text-white"
            }`}
            data-testid={`widget-inline-label-${id}`}
            title={label}
          >
            <GripVertical className="w-3 h-3" />
            <span className="truncate max-w-[140px]">{label}</span>
          </div>
          <button
            type="button"
            onClick={() => setHidden(id, true)}
            aria-label={bg ? `Премахни ${label}` : `Remove ${label}`}
            data-testid={`widget-inline-remove-${id}`}
            className={`pointer-events-auto inline-flex items-center justify-center w-7 h-7 rounded-full shadow-lg transition-transform hover:scale-110 ${
              isDark
                ? "bg-rose-500 text-white hover:bg-rose-400"
                : "bg-rose-600 text-white hover:bg-rose-500"
            }`}
            title={bg ? "Скрий тази секция" : "Hide this section"}
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>
      )}
      {children}
    </As>
  );
}
